import { useEffect, useRef, useState } from 'react';
import { Avatar, Menu, MenuItem } from '@mui/material';
import { Lock } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  SnackbarCloseReason,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AccountCircle, Palette, Visibility, VisibilityOff } from '@mui/icons-material';
import * as yup from 'yup';
import { useAppThemeContext, useAuthContext } from '../../contexts';
import { useBloqueioSalvamento } from '../../hooks';
import { TelaBloqueioSalvando } from '../feedback/TelaBloqueioSalvando';
import { THEME_OPTIONS, LOGIN_ACCENT, LOGIN_BUTTON_ACCENT } from '../../themes';

const loginSchema = yup.object().shape({
  usuario: yup.string().required('Usuário é obrigatório'),
  senha: yup.string().required('Senha é obrigatória').min(5, 'Mínimo 5 caracteres'),
});

const REMEMBER_ME_STORAGE_KEY = 'APP_REMEMBERED_USER';

interface ILoginProps {
  children: React.ReactNode;
}

export const Login: React.FC<ILoginProps> = ({ children }) => {
  const { isAuthenticated, login } = useAuthContext();
  const { themeName, setThemeName } = useAppThemeContext();
  const isDark = themeName === 'dark';
  const accentColor = LOGIN_ACCENT[themeName];
  const buttonAccent = LOGIN_BUTTON_ACCENT[themeName];

  const [anchorElTema, setAnchorElTema] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress, iniciar: iniciarLogin, concluir: concluirLogin, cancelar: cancelarLogin } = useBloqueioSalvamento();
  const [senhaError, setSenhaError] = useState('');
  const [usuarioError, setUsuarioError] = useState('');
  const [senha, setSenha] = useState('');
  const [usuario, setUsuario] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const senhaInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const rememberedUser = localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
    if (rememberedUser) {
      setUsuario(rememberedUser);
      setRememberMe(true);
    }
  }, []);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const handleSubmit = () => {
    if (!iniciarLogin()) return;

    setIsLoading(true);
    setSenhaError('');
    setUsuarioError('');

    loginSchema
      .validate({ usuario: usuario.trim(), senha: senha.trim() }, { abortEarly: false })
      .then(dadosValidados => {
        login(dadosValidados.usuario, dadosValidados.senha)
          .then(() => {
            if (rememberMe) {
              localStorage.setItem(REMEMBER_ME_STORAGE_KEY, dadosValidados.usuario);
            } else {
              localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
            }
            setIsLoading(false);
            concluirLogin();
          })
          .catch((error) => {
            setIsLoading(false);
            cancelarLogin();
            setTms('error');
            setMsg(error?.message || 'Usuário ou senha incorretos');
            setOpenmsg(true);
          });
      })
      .catch((errors: yup.ValidationError) => {
        setIsLoading(false);
        cancelarLogin();
        errors.inner.forEach(error => {
          if (error.path === 'usuario') setUsuarioError(error.message);
          if (error.path === 'senha') setSenhaError(error.message);
        });
      });
  };

  if (isAuthenticated) return <>{children}</>;

  const glassCardSx = isDark
    ? {
      background: 'rgba(255, 255, 255, 0.12)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
    }
    : {
      background: 'rgba(255, 255, 255, 0.65)',
      border: '1px solid rgba(255, 255, 255, 0.8)',
    };

  const fieldIconSx = {
    width: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: accentColor,
    color: '#ffffff',
    '& svg': { fontSize: 28 },
  };

  const fieldInputSx = {
    flex: 1,
    '& .MuiFilledInput-root': {
      bgcolor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.55)',
      color: isDark ? '#ffffff' : '#1e293b',
      fontSize: '1.15rem',
      '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.7)' },
      '&.Mui-focused': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.7)' },
      '&::before, &::after': { display: 'none' },
    },
    '& .MuiFilledInput-input': {
      py: '22px',
      '&::placeholder': {
        color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.6)',
        opacity: 1,
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.85rem',
    },
  };

  return (
    <Box
      width="100vw"
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        background: isDark
          ? 'linear-gradient(135deg, #263449 0%, #1e2a3a 45%, #17212e 75%, #12181f 100%)'
          : 'linear-gradient(135deg, #eef1f6 0%, #e4e9f1 45%, #dde3ed 75%, #d7dee9 100%)',
      }}
    >
      <TelaBloqueioSalvando open={isSaving} message="Entrando..." progress={progress} />

      <Tooltip title="Tema">
        <IconButton
          onClick={(e) => setAnchorElTema(e.currentTarget)}
          sx={{
            position: 'absolute',
            top: 24,
            right: 24,
            color: isDark ? '#ffffff' : accentColor,
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.6)',
            '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.8)' },
          }}
        >
          <Palette />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchorElTema} open={Boolean(anchorElTema)} onClose={() => setAnchorElTema(null)}>
        {THEME_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.name}
            selected={themeName === opt.name}
            onClick={() => {
              setThemeName(opt.name);
              setAnchorElTema(null);
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: opt.swatch,
                mr: 1.5,
                border: '1px solid rgba(0,0,0,0.15)',
              }}
            />
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 3000}
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <Box position="relative" width={440}>
        <Box
          display="flex"
          justifyContent="center"
          sx={{ position: 'absolute', top: -60, left: 0, right: 0, zIndex: 2 }}
        >
          <Avatar
            sx={{
              bgcolor: accentColor,
              width: 110,
              height: 110,
              border: '4px solid rgba(255, 255, 255, 0.5)',
              boxShadow: 6,
            }}
          >
            <AccountCircle sx={{ fontSize: 68, color: '#ffffff' }} />
          </Avatar>
        </Box>

        <Box
          sx={{
            ...glassCardSx,
            pt: 10,
            pb: 5,
            px: 5,
            borderRadius: 6,
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            gap: 3.5,
          }}
        >
          <Box display="flex" borderRadius={3} overflow="hidden" boxShadow={2}>
            <Box sx={fieldIconSx}>
              <AccountCircle />
            </Box>
            <TextField
              fullWidth
              autoFocus
              variant="filled"
              placeholder="Usuário"
              hiddenLabel
              value={usuario}
              disabled={isLoading || isSaving}
              error={!!usuarioError}
              helperText={usuarioError}
              onKeyDown={e => {
                setUsuarioError('');
                if (e.key === 'Enter') {
                  e.preventDefault();
                  senhaInputRef.current?.focus();
                }
              }}
              onChange={e => setUsuario(e.target.value)}
              sx={fieldInputSx}
            />
          </Box>

          <Box display="flex" borderRadius={3} overflow="hidden" boxShadow={2}>
            <Box sx={fieldIconSx}>
              <Lock />
            </Box>
            <TextField
              fullWidth
              variant="filled"
              placeholder="Senha"
              hiddenLabel
              type={mostrarSenha ? 'text' : 'password'}
              value={senha}
              disabled={isLoading || isSaving}
              error={!!senhaError}
              helperText={senhaError}
              inputRef={senhaInputRef}
              onKeyDown={e => {
                setSenhaError('');
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              onChange={e => setSenha(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarSenha(old => !old)}
                      edge="end"
                      tabIndex={-1}
                      sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.6)' }}
                    >
                      {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldInputSx}
            />
          </Box>

          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  disabled={isLoading || isSaving}
                  onChange={e => setRememberMe(e.target.checked)}
                  sx={{ color: isDark ? '#ffffff' : accentColor, '& svg': { fontSize: 26 } }}
                />
              }
              label={
                <Typography variant="body1" sx={{ color: isDark ? '#ffffff' : '#1e293b' }}>
                  Lembrar-me
                </Typography>
              }
            />
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" sx={{ mt: -3, position: 'relative', zIndex: 3 }}>
          <Button
            variant="contained"
            disabled={isLoading || isSaving}
            onClick={handleSubmit}
            endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
            sx={{
              width: '65%',
              py: 1.6,
              borderRadius: 999,
              fontWeight: 600,
              fontSize: '1.1rem',
              letterSpacing: 1,
              bgcolor: buttonAccent.bg,
              color: '#ffffff',
              backdropFilter: isDark ? 'blur(10px)' : undefined,
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
              '&:hover': {
                bgcolor: buttonAccent.hover,
              },
            }}
          >
            Entrar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
